import React, { useEffect, useState } from 'react';
import { XIcon, PhoneIcon, MailIcon, MapPinIcon, PlusIcon, SendIcon, CalendarIcon, ClipboardListIcon, ChevronRightIcon } from 'lucide-react';
import { AnyContact } from '../types';
interface InlineDetailPanelProps {
  contact: AnyContact | null;
  onClose: () => void;
  onAddNote?: (contactId: string, note: string) => void;
  isPowerDialing?: boolean;
  onSaveAndNext?: (data: {
    contactId: string;
    nextAction: string;
    followUpDate: string;
  }) => void;
}
export function InlineDetailPanel({
  contact,
  onClose,
  onAddNote,
  isPowerDialing = false,
  onSaveAndNext
}: InlineDetailPanelProps) {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  // Disposition form state
  const [nextAction, setNextAction] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  // Reset disposition form when contact changes
  useEffect(() => {
    setNextAction('');
    setFollowUpDate('');
    setIsAddingNote(false);
    setNoteText('');
  }, [contact?.id]);
  if (!contact) return null;
  const handleSubmitNote = () => {
    if (noteText.trim() && onAddNote) {
      onAddNote(contact.id, noteText.trim());
      setNoteText('');
      setIsAddingNote(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitNote();
    }
    if (e.key === 'Escape') {
      setNoteText('');
      setIsAddingNote(false);
    }
  };
  const canSaveAndNext = nextAction.trim() !== '' && followUpDate !== '';
  const handleSaveAndNext = () => {
    if (canSaveAndNext && onSaveAndNext) {
      onSaveAndNext({
        contactId: contact.id,
        nextAction: nextAction.trim(),
        followUpDate
      });
    }
  };
  const contactLabel = contact.type === 'lead' ? 'Lead' : contact.type === 'loan' ? 'Loan' : 'Retention';
  return <div className="h-full flex flex-col bg-olympian-surface/30" data-id="element-65">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-olympian-border flex justify-between items-center bg-olympian-surface shrink-0" data-id="element-66">
        <h2 className="font-semibold text-theme flex items-center gap-2 text-sm" data-id="element-67">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]" data-id="element-68" />
          {contactLabel} Record
        </h2>
        {!isPowerDialing && <button onClick={onClose} className="text-olympian-textMuted hover:text-theme transition-colors p-1 rounded-md hover:bg-olympian-navy" aria-label="Close panel" data-id="element-69">
            <XIcon className="w-4 h-4" data-id="element-70" />
          </button>}
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar" data-id="element-71">
        {/* Contact Info */}
        <div className="p-5 border-b border-olympian-border" data-id="element-72">
          <div className="w-14 h-14 bg-olympian-navy border border-olympian-border text-olympian-gold rounded-full flex items-center justify-center text-xl font-bold mb-4" data-id="element-73">
            {contact.name.charAt(0)}
          </div>
          <h1 className="text-xl font-bold text-theme mb-0.5" data-id="element-74">
            {contact.name}
          </h1>
          <span className="inline-block px-2 py-0.5 bg-olympian-navyLight border border-olympian-border rounded text-[10px] font-bold uppercase tracking-wider text-olympian-textMuted mb-5" data-id="element-75">
            {contactLabel}
          </span>

          <div className="space-y-3" data-id="element-76">
            <ContactInfoRow icon={<PhoneIcon className="w-3.5 h-3.5 text-olympian-gold" data-id="element-78" />} data-id="element-77">
              <a href={`tel:${contact.phone}`} className="text-theme hover:text-olympian-gold font-mono text-sm" data-id="element-79">
                {contact.phone}
              </a>
            </ContactInfoRow>
            <ContactInfoRow icon={<MailIcon className="w-3.5 h-3.5 text-olympian-gold" data-id="element-81" />} data-id="element-80">
              <a href={`mailto:${contact.email}`} className="text-theme hover:text-olympian-gold text-sm truncate" data-id="element-82">
                {contact.email}
              </a>
            </ContactInfoRow>
            {contact.address && <ContactInfoRow icon={<MapPinIcon className="w-3.5 h-3.5 text-olympian-gold" data-id="element-84" />} data-id="element-83">
                <span className="text-theme text-sm" data-id="element-85">{contact.address}</span>
              </ContactInfoRow>}
          </div>
        </div>

        {/* Power Dial: Disposition Form */}
        {isPowerDialing ? <div className="p-5 border-b border-olympian-border bg-olympian-surface/50" data-id="element-86">
            <h3 className="text-[10px] font-bold text-olympian-textMuted uppercase tracking-wider mb-4" data-id="element-87">
              Call Notes <span className="text-red-400" data-id="element-88">*</span>
            </h3>

            {/* Next Action */}
            <div className="mb-4" data-id="element-89">
              <label className="block text-[10px] font-bold text-olympian-textMuted uppercase tracking-wider mb-2" data-id="element-90">
                Next Action <span className="text-red-400" data-id="element-91">*</span>
              </label>
              <div className="relative" data-id="element-92">
                <ClipboardListIcon className="absolute left-3 top-2.5 w-4 h-4 text-olympian-textMuted" data-id="element-93" />
                <textarea rows={2} value={nextAction} onChange={e => setNextAction(e.target.value)} placeholder="e.g. Send pre-approval letter, Call back Tuesday..." className="w-full pl-10 pr-3 py-2.5 bg-olympian-navy border border-olympian-border rounded-lg text-sm text-theme focus:ring-1 focus:ring-olympian-gold focus:border-olympian-gold outline-none resize-none placeholder-olympian-textMuted/50" data-id="element-94" />
              </div>
            </div>

            {/* Follow-up Date */}
            <div className="mb-5" data-id="element-95">
              <label className="block text-[10px] font-bold text-olympian-textMuted uppercase tracking-wider mb-2" data-id="element-96">
                Follow-up Date <span className="text-red-400" data-id="element-97">*</span>
              </label>
              <div className="relative" data-id="element-98">
                <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-olympian-textMuted" data-id="element-99" />
                <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} className="w-full pl-10 pr-3 py-2.5 bg-olympian-navy border border-olympian-border rounded-lg text-sm text-theme focus:ring-1 focus:ring-olympian-gold focus:border-olympian-gold outline-none [color-scheme:dark]" data-id="element-100" />
              </div>
            </div>

            {/* Save & Next */}
            <button onClick={handleSaveAndNext} disabled={!canSaveAndNext} className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold transition-all ${canSaveAndNext ? 'bg-olympian-gold text-olympian-navy hover:bg-olympian-goldHover shadow-[0_0_12px_rgba(212,168,67,0.25)]' : 'bg-olympian-navy text-olympian-textMuted border border-olympian-border cursor-not-allowed opacity-60'}`} data-id="element-101">
              <ChevronRightIcon className="w-4 h-4" data-id="element-102" />
              Save & Next
            </button>

            {!canSaveAndNext && <p className="text-[10px] text-olympian-textMuted text-center mt-2" data-id="element-103">
                Complete all fields above to continue
              </p>}
          </div> /* Normal mode: Leave Note */ : <div className="p-5 border-b border-olympian-border bg-olympian-surface/50" data-id="element-104">
            <h3 className="text-[10px] font-bold text-olympian-textMuted uppercase tracking-wider mb-3" data-id="element-105">
              Quick Actions
            </h3>
            {isAddingNote ? <div className="space-y-2.5" data-id="element-106">
                <textarea autoFocus rows={3} value={noteText} onChange={e => setNoteText(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type your note... (Enter to save, Esc to cancel)" className="w-full p-3 bg-olympian-navy border border-olympian-border rounded-lg text-sm text-theme focus:ring-1 focus:ring-olympian-gold focus:border-olympian-gold outline-none resize-none placeholder-olympian-textMuted/50" data-id="element-107" />
                <div className="flex gap-2" data-id="element-108">
                  <button onClick={handleSubmitNote} disabled={!noteText.trim()} className="flex-1 flex items-center justify-center gap-2 bg-olympian-gold text-olympian-navy rounded-lg py-2 text-sm font-bold hover:bg-olympian-goldHover transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-109">
                    <SendIcon className="w-3.5 h-3.5" data-id="element-110" /> Save Note
                  </button>
                  <button onClick={() => {
              setNoteText('');
              setIsAddingNote(false);
            }} className="px-4 py-2 bg-olympian-navy border border-olympian-border rounded-lg text-sm font-medium text-olympian-textMuted hover:text-theme transition-colors" data-id="element-111">
                    Cancel
                  </button>
                </div>
              </div> : <button onClick={() => setIsAddingNote(true)} className="w-full flex items-center justify-center gap-2 bg-olympian-gold text-olympian-navy rounded-lg py-2.5 text-sm font-bold hover:bg-olympian-goldHover transition-colors" data-id="element-112">
                <PlusIcon className="w-4 h-4" data-id="element-113" /> Leave Note
              </button>}
          </div>}

        {/* History Timeline */}
        <div className="p-5" data-id="element-114">
          <h3 className="text-[10px] font-bold text-olympian-textMuted uppercase tracking-wider mb-5" data-id="element-115">
            Recent History
          </h3>
          <div className="space-y-0" data-id="element-116">
            {contact.history.map((item, idx) => <div key={item.id} className={`relative pl-5 border-l border-olympian-border pb-5 ${idx === contact.history.length - 1 ? 'pb-0 border-transparent' : ''}`} data-id="element-117">
                <div className="absolute -left-[4px] top-0.5 w-2 h-2 rounded-full bg-olympian-surface border-2 border-olympian-gold" data-id="element-118" />
                <div className="text-[11px] text-olympian-textMuted mb-0.5 font-mono" data-id="element-119">
                  {new Date(item.date).toLocaleDateString()} •{' '}
                  {new Date(item.date).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
                </div>
                <div className="text-sm text-theme leading-relaxed" data-id="element-120">
                  <span className="font-semibold text-olympian-gold" data-id="element-121">
                    {item.type.charAt(0).toUpperCase() + item.type.slice(1)}:
                  </span>{' '}
                  {item.description}
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
function ContactInfoRow({
  icon,
  children
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return <div className="flex items-center gap-3 min-w-0" data-id="element-122">
      <div className="w-7 h-7 rounded-md bg-olympian-surface flex items-center justify-center border border-olympian-border shrink-0" data-id="element-123">
        {icon}
      </div>
      <div className="min-w-0 truncate" data-id="element-124">{children}</div>
    </div>;
}